import { type WindowSprig } from '@sprig-technologies/sprig-browser';

import { USER_ID_LOCAL_STORAGE_KEY } from 'constants/session';
import { getSessionStorageItem, setSessionStorageItem } from 'utils/storage';

type SprigAttributeValue = string | number | boolean;
type SprigAttributes = Record<string, SprigAttributeValue>;
type SprigShim = ((command: string, ...params: unknown[]) => void) & {
    _queue: unknown[][];
    appId: string;
};
type SprigSoftReloadOptions = {
    onSoftReloadFailed?: () => void;
    onSoftReloadQueued?: () => void;
};

const SPRIG_SHIM_SRC = 'https://cdn.sprig.com/shim.js';

// https://docs.sprig.com/docs/web-javascript#install-with-html-recommended
export const getSprigScriptContent = (): string => {
    const environmentId = process.env.NEXT_PUBLIC_SPRIG_ENVIRONMENT_ID;
    if (!environmentId) {
        return '';
    }
    return `
        (function(l,e,a,p) {
            if (window.Sprig) return;
            window.Sprig = function(){S._queue.push(arguments)}
            var S = window.Sprig;S.appId = a;S._queue = [];window.UserLeap=S;
            a=l.createElement('script');
            a.async=1;a.src=e+'?id='+S.appId;
            p=l.getElementsByTagName('script')[0];
            p.parentNode.insertBefore(a, p);
        })(document, '${SPRIG_SHIM_SRC}', '${environmentId}');
    `;
};

const isSprigReady = (sprig: WindowSprig | undefined): sprig is WindowSprig => {
    return !!sprig && typeof sprig.identifyAndTrack === 'function';
};

// since the script lazily loads, we need to wait for Sprig to be ready
export const getSprig = (callback: (sprig: WindowSprig) => void): (() => void) => {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const sprig = (window as any).Sprig as WindowSprig | undefined;

    if (isSprigReady(sprig)) {
        callback(sprig);
        return () => {};
    }

    const intervalId = setInterval(() => {
        const sprig = (window as any).Sprig as WindowSprig | undefined;
        if (isSprigReady(sprig)) {
            clearInterval(intervalId);
            callback(sprig);
        }
    }, 100);

    return () => clearInterval(intervalId);
};

export const setSprigUserId = (userId: string): (() => void) => {
    return getSprig((Sprig) => Sprig.setUserId(userId));
};

export const logoutSprigUser = (): (() => void) => {
    return getSprig((Sprig) => Sprig.logoutUser());
};

export const trackSprigEvent = (payload: TrackPayload): (() => void) => {
    return getSprig((Sprig) => {
        if (typeof window !== 'undefined' && payload.userId === undefined) {
            payload.userId =
                typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function'
                    ? localStorage.getItem(USER_ID_LOCAL_STORAGE_KEY) || undefined
                    : undefined;
        }
        Sprig.identifyAndTrack(payload);
    });
};

export const setSprigAttribute = (name: string, value: SprigAttributeValue): (() => void) => {
    return getSprig((Sprig) => Sprig.setAttribute(name, value));
};

const getStoredUserId = (): string | undefined => {
    if (typeof localStorage === 'undefined' || typeof localStorage.getItem !== 'function') return undefined;
    return localStorage.getItem(USER_ID_LOCAL_STORAGE_KEY) || undefined;
};

const getCurrentSprigUserId = (Sprig: WindowSprig): string | undefined => {
    return Sprig.userId || getStoredUserId();
};

const getSprigEnvironmentId = (Sprig: WindowSprig): string | undefined => {
    return Sprig.appId || Sprig.envId || Sprig.environmentId || process.env.NEXT_PUBLIC_SPRIG_ENVIRONMENT_ID;
};

const createSprigShim = (environmentId: string): SprigShim => {
    const queue: unknown[][] = [];
    const Sprig = ((...args: unknown[]) => {
        queue.push(args);
    }) as SprigShim;
    Sprig.appId = environmentId;
    Sprig._queue = queue;
    return Sprig;
};

const insertSprigShimScript = (environmentId: string): void => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `${SPRIG_SHIM_SRC}?id=${environmentId}`;
    script.setAttribute('data-sprig-soft-reload', 'true');

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
        return;
    }

    document.head.appendChild(script);
};

const softReloadSprig = (environmentId: string, userId: string | undefined, attributes: SprigAttributes): void => {
    const Sprig = createSprigShim(environmentId);
    (window as unknown as { Sprig: SprigShim; UserLeap: SprigShim }).Sprig = Sprig;
    (window as unknown as { Sprig: SprigShim; UserLeap: SprigShim }).UserLeap = Sprig;

    if (userId) {
        Sprig('setUserId', userId);
    }
    Sprig('setAttributes', attributes);
    insertSprigShimScript(environmentId);
};

export const setSprigAttributeAndSoftReload = (
    name: string,
    value: SprigAttributeValue,
    options?: SprigSoftReloadOptions,
): (() => void) => {
    let didCleanup = false;
    const cleanupSprig = getSprig((Sprig) => {
        if (didCleanup) return;

        void Promise.resolve(Sprig.setAttribute(name, value))
            .then(() => {
                if (didCleanup) return;

                try {
                    const environmentId = getSprigEnvironmentId(Sprig);
                    if (!environmentId) {
                        options?.onSoftReloadFailed?.();
                        return;
                    }

                    const userId = getCurrentSprigUserId(Sprig);
                    Sprig.teardown();
                    softReloadSprig(environmentId, userId, { [name]: value });
                    options?.onSoftReloadQueued?.();
                } catch {
                    options?.onSoftReloadFailed?.();
                }
            })
            .catch(() => {
                if (didCleanup) return;
                options?.onSoftReloadFailed?.();
            });
    });

    return () => {
        didCleanup = true;
        cleanupSprig();
    };
};

const trackedOnceSessionKeys = new Set<string>();

/**
 * Fires a Sprig event at most once per browser session per user. Dedup is guarded
 * by both an in-memory Set (so a tab whose sessionStorage write fails — private
 * mode / quota — still won't re-fire) and sessionStorage (so it stays deduped
 * across reloads within the session). Returns trackSprigEvent's cleanup so a
 * caller's effect can cancel the pending Sprig-ready poll on unmount, or a no-op
 * when the event was already tracked.
 */
export const trackSprigEventOnce = (eventName: string, userId: string): (() => void) => {
    const sessionKey = `sprigTrackedEventOnce:${eventName}:${userId}`;
    if (trackedOnceSessionKeys.has(sessionKey) || getSessionStorageItem(sessionKey) === 'true') {
        return () => {};
    }

    trackedOnceSessionKeys.add(sessionKey);
    setSessionStorageItem(sessionKey, 'true');
    return trackSprigEvent({ eventName, userId });
};
